import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import Mascot from "../ZETY-MASCOT/Mascot";
import { OPEN_CHAT_EVENT } from "../ZETY-MASCOT/chatBus";
import useContent from "../hooks/useContent";
import "./HomeFAQ.css";

/**
 * Homepage FAQ — answer-engine optimisation (2026 SEO).
 *
 * Google's AI Overviews, Bing Copilot and voice assistants increasingly
 * answer questions directly instead of listing links. Pages that carry
 * the question + a concise, factual answer in plain HTML (and mirror it
 * in FAQPage structured data — emitted from Home.jsx via <Seo />) are
 * the ones those engines quote. Every answer here is grounded in the
 * same facts as the main /faqs page — never invent claims.
 */
export const HOME_FAQS = [
  {
    question: "Is Zetech University accredited?",
    answer:
      "Yes. Zetech University is a fully chartered private university in Kenya, accredited by the Commission for University Education (CUE), and is ISO 9001:2015 certified.",
  },
  {
    question: "Which courses does Zetech University offer?",
    answer:
      "Zetech University offers Certificate, Diploma, Degree, Masters, PhD and TVET programmes across Business, ICT, Engineering, Health Sciences, Media, Education, Hospitality, Law and more.",
  },
  {
    question: "When are the intakes at Zetech University?",
    answer:
      "The university admits students in three intakes every year: January, May and September. Applications are open throughout the year ahead of each intake.",
  },
  {
    question: "How do I apply to Zetech University?",
    answer:
      "Apply online through the Sajili admissions portal: fill in the application form, upload your documents and submit. Successful applicants receive admission updates by email.",
  },
  {
    question: "Where are Zetech University campuses located?",
    answer:
      "Zetech University has campuses in Ruiru (off Thika Road) and Mang'u (Technology Park, Thika–Mang'u Road), plus the Zetech Digital School for online learning.",
  },
  {
    question: "What study modes are available?",
    answer:
      "Students can study full-time, through blended learning (a mix of classroom and online teaching), or fully online via the Zetech Digital School, depending on the programme.",
  },
];

export default function HomeFAQ() {
  const [open, setOpen] = useState(0);

  // Editable in the admin dashboard (Site Content > Homepage FAQs); the list
  // above stays as the fallback when nothing is published.
  const { items } = useContent("home_faqs", []);
  const faqs = useMemo(
    () =>
      items.length
        ? items.map((row) => ({ question: row.title, answer: row.description || "" }))
        : HOME_FAQS,
    [items]
  );

  return (
    <section className="home-faq" aria-labelledby="home-faq-title">
      <div className="home-faq__container">
        <div className="home-faq__mascot" aria-hidden="true">
          <Mascot
            message="Hi! 👋
Got a question?
Ask Techizen AI!"
            onToggleChat={() =>
              window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT))
            }
          />
        </div>

        <div className="home-faq__header">
          {/* <span className="home-faq__eyebrow">Frequently Asked Questions</span> */}
          <h2 id="home-faq-title">
            Everything You Need to Know About Studying at Zetech University
          </h2>
          {/* <p>
            Quick answers to the questions prospective students ask us most —
            accreditation, courses, intakes, applying, campuses and study
            modes.
          </p> */}
        </div>

        <div className="home-faq__list">
          {faqs.map((faq, index) => {
            const isOpen = open === index;
            return (
              <div
                className={`home-faq__item ${isOpen ? "home-faq__item--open" : ""}`}
                key={faq.question}
              >
                <h3 className="home-faq__question-heading">
                  <button
                    type="button"
                    className="home-faq__question"
                    aria-expanded={isOpen}
                    aria-controls={`home-faq-answer-${index}`}
                    onClick={() => setOpen(isOpen ? -1 : index)}
                  >
                    <span>{faq.question}</span>
                    <FaChevronDown
                      className="home-faq__chevron"
                      aria-hidden="true"
                    />
                  </button>
                </h3>
                <div
                  id={`home-faq-answer-${index}`}
                  className="home-faq__answer"
                  role="region"
                  hidden={!isOpen}
                >
                  <p>{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>

        <Link to="/faqs" className="home-faq__more">
          View all frequently asked questions
        </Link>

        <aside className="home-faq__whistleblowing" aria-labelledby="home-faq-whistleblowing-title">
          <div className="home-faq__whistleblowing-copy">
            <span className="home-faq__whistleblowing-eyebrow">Help protect our community</span>
            <h3 id="home-faq-whistleblowing-title">See Something, Say Something</h3>
            <h4>Don’t Wait—Act Early</h4>
            <p>Report immediately when you:</p>
            <ul>
              <li>Suspect fraud or corruption</li>
              <li>Witness unethical or illegal behaviour</li>
              <li>Notice misuse of University resources</li>
              <li>Become aware of conflicts of interest or abuse</li>
            </ul>
            <p className="home-faq__whistleblowing-note">
              Early reporting helps prevent harm and protects the University community.
            </p>
          </div>
          <div className="home-faq__whistleblowing-actions">
            <a className="home-faq__report-email" href="mailto:fraud@zetech.ac.ke">
              Report by email <span>fraud@zetech.ac.ke</span>
            </a>
            <Link className="home-faq__report-link" to="/whistle-blowing">
              Visit the whistle-blowing page <span aria-hidden="true">→</span>
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
}
